import {
  RecordContextProvider,
  useListContext,
  useTimeout,
  useTranslate,
} from "ra-core";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { RotateCcw } from "lucide-react";

import { BCSyncIcon } from "../misc/BCSyncBadge";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company } from "../types";
import { CompanyAvatar } from "./CompanyAvatar";
import { CompanyContactAvatarGroup } from "./CompanyContactAvatarGroup";

export const CompanyListContent = () => {
  const translate = useTranslate();
  const { data: companies, error, isPending } = useListContext<Company>();

  if (isPending) {
    return <Skeleton className="w-full h-9" />;
  }

  if (error) {
    return null;
  }

  return (
    <div className="md:divide-y">
      {companies.map((company) => (
        <RecordContextProvider key={company.id} value={company}>
          <CompanyListItem company={company} />
        </RecordContextProvider>
      ))}

      {companies.length === 0 && (
        <div className="p-4">
          <div className="text-muted-foreground">
            {translate("resources.companies.empty.title", {
              _: "No customers found",
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const CompanyListItem = ({ company }: { company: Company }) => {
  const translate = useTranslate();
  const { companySectors } = useConfigurationContext();

  const sectorLabel = companySectors.find((s) => s.value === company.sector)
    ?.label;
  const subtitleParts: string[] = [];

  if (sectorLabel) {
    subtitleParts.push(sectorLabel);
  }
  if (company.nb_contacts) {
    subtitleParts.push(
      translate("resources.companies.nb_contacts", {
        smart_count: company.nb_contacts,
        _: "%{smart_count} contact |||| %{smart_count} contacts",
      }),
    );
  }

  return (
    <Link
      to={`/companies/${company.id}/show`}
      className="flex flex-row items-center gap-4 py-2 md:py-3 md:px-4 hover:bg-muted transition-colors md:first:rounded-t-xl md:last:rounded-b-xl"
    >
      <CompanyAvatar />
      <div className="flex-1 min-w-0">
        <div className="font-medium flex items-center gap-1.5">
          {company.name}
          {company.bc_synced && <BCSyncIcon />}
        </div>
        {subtitleParts.length > 0 && (
          <div className="text-sm text-muted-foreground truncate">
            {subtitleParts.join(" · ")}
          </div>
        )}
      </div>
      <div className="shrink-0 ml-2">
        <CompanyContactAvatarGroup nbContacts={company.nb_contacts} />
      </div>
    </Link>
  );
};

export const CompanyListContentMobile = () => {
  const translate = useTranslate();
  const {
    data: companies,
    error,
    isPending,
    refetch,
  } = useListContext<Company>();
  const oneSecondHasPassed = useTimeout(1000);

  if (isPending) {
    if (!oneSecondHasPassed) {
      return null;
    }
    return (
      <>
        {[...Array(5)].map((_, index) => (
          <div
            key={index}
            className="flex flex-row items-center gap-4 py-2 hover:bg-muted transition-colors"
          >
            <Skeleton className="w-10 h-10 rounded-full" />
            <div className="flex-1 min-w-0">
              <Skeleton className="w-32 h-5 mb-2" />
              <Skeleton className="w-48 h-4" />
            </div>
          </div>
        ))}
      </>
    );
  }

  if (error && !companies) {
    return (
      <div className="p-4">
        <div className="text-center text-muted-foreground mb-4">
          {translate("ra.message.error", {
            _: "Error loading customers",
          })}
        </div>
        <div className="text-center mt-2">
          <Button onClick={() => refetch()}>
            <RotateCcw />
            {translate("crm.common.retry")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="md:divide-y">
      {companies.map((company) => (
        <RecordContextProvider key={company.id} value={company}>
          <CompanyListItem company={company} />
        </RecordContextProvider>
      ))}
      {companies.length === 0 && (
        <div className="p-4">
          <div className="text-muted-foreground">
            {translate("resources.companies.empty.title", {
              _: "No customers found",
            })}
          </div>
        </div>
      )}
    </div>
  );
};
